import React, { useState, useEffect } from 'react';
import { useMsal } from '@azure/msal-react';
import { environment } from '../../environments/environment';
import LoginError from '../LoginError/LoginError';
import LoginLoading from '../LoginLoading/LoginLoading';
import StoreUnavailable from '../StoreUnavailable/StoreUnavailable';
import './Login.scss';
import { AuthenticationResult } from '@azure/msal-browser';

const Login = () => {
  const { instance, accounts } = useMsal();

  const isAuthenticated = accounts && accounts.length > 0;

  const [retry, setRetry] = useState<number>(1);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [userInfo, setUserInfo] = useState<any>(undefined);

  const [signInError, setSignInError] = useState<{
    message: string;
    displayToUser: boolean;
    retryable: boolean;
    clearCache: boolean;
  }>({
    message: '',
    displayToUser: false,
    retryable: false,
    clearCache: false,
  });

  const [storeAvailable, setStoreAvailable] = useState<{
    status: boolean;
    temporary: boolean;
  }>({ status: true, temporary: false });

  useEffect(() => {
    const fetchUserData = async (token: string) => {
      const headers = new Headers();
      const bearer = `Bearer ${token}`;

      headers.append('Authorization', bearer);

      const options = {
        method: 'GET',
        headers: headers,
      };

      const detailsToInclude = [
        'displayName',
        'givenName',
        'jobTitle',
        'mail',
        'mobilePhone',
        'officeLocation',
        'preferredLanguage',
        'surname',
        'userPrincipalName',
        'employeeId',
        'country',
        'city',
        'state',
      ];

      return fetch(
        `https://graph.microsoft.com/v1.0/me?$select=${detailsToInclude.join(
          ','
        )}`,
        options
      )
        .then((response) => response.json())
        .catch((error) => console.log(error));
    };

    const msalLogin = async () => {
      console.log(
        'Inside useEffect, isAuthenticated:',
        isAuthenticated,
        'retry:',
        retry
      );
      if (!isAuthenticated) {
        // Initiate login redirect if not authenticated
        instance.loginRedirect();
        return;
      }

      // Acquire token silently using minimal scopes
      const request = { scopes: ['openid', 'profile', 'email', 'User.Read', 'User.Read.All'] };
      try {

        let response: AuthenticationResult | null = null;
        try {
          response = await instance.acquireTokenSilent(request);
        } catch (err) {
          console.error('Failed to acquire token silently: ', err);
          await instance.acquireTokenRedirect(request);
        }
        if (!response) {
          throw new Error('Failed to acquire token');
        }

        const accessToken = response.accessToken;
        const userData = await fetchUserData(accessToken);
        setUserInfo(userData);

        const returnTo: string = sessionStorage.getItem('return_to') || '';
        const apiResponse = await fetch(
          `${environment.serviceUrl}/v1/shopify/multipass/login-url?return_to=${returnTo}`,
          { headers: { Authorization: `Bearer ${accessToken}` } }
        );
        if (!apiResponse.ok) {
          const errorData = await apiResponse.json();
          throw errorData.error || errorData;
        }
        const data = await apiResponse.json();
        window.location.href = data.data;
      } catch (err: unknown) {
        console.error('Failed to get login url: ', err);
        if (err && typeof err === 'object' && 'statusCode' in err) {
          switch ((err as { statusCode: number; }).statusCode) {
            case 400.101:
              setSignInError({
                message: 'There is no country configured in your profile.',
                displayToUser: true,
                retryable: false,
                clearCache: false,
              });
              break;
            case 400.102:
              setStoreAvailable({ status: false, temporary: false });
              break;
            case 400.103:
              setStoreAvailable({ status: false, temporary: true });
              break;
            case 400.104:
              setSignInError({
                message: 'Your country is not yet activated for store access.',
                displayToUser: true,
                retryable: false,
                clearCache: false,
              });
              break;
            case 400.105:
              setSignInError({
                message:
                  'Your account is not yet enabled to access Online Game Store.',
                displayToUser: true,
                retryable: false,
                clearCache: false,
              });
              break;
            case 400.106:
              setSignInError({
                message:
                  'Your Employee Type is not yet enabled to access Online Game Store.',
                displayToUser: true,
                retryable: false,
                clearCache: false,
              });
              break;
            case 400.11:
              if (retry < 10) {
                setTimeout(() => {
                  console.log('Retrying...', retry);
                  setSignInError({
                    message: '',
                    displayToUser: false,
                    retryable: false,
                    clearCache: false,
                  });
                  setRetry(retry + 1);
                }, 1000);
              } else {
                setSignInError({
                  message: 'Failed in communicating with Shopify server',
                  displayToUser: false,
                  retryable: false,
                  clearCache: false,
                });
              }
              break;
            case 400.12:
              setSignInError({
                message: 'Failed at iHub',
                displayToUser: false,
                retryable: false,
                clearCache: false,
              });
              break;
            default:
              setSignInError({
                message: (err instanceof Error && err.message) || String(err),
                displayToUser: false,
                retryable: false,
                clearCache: false,
              });
          }
        } else {
          if (err instanceof Error && err.message === 'Unauthorized') {
            setSignInError({
              message: '',
              displayToUser: false,
              retryable: false,
              clearCache: true,
            });
          } else {
            setSignInError({
              message: (err instanceof Error && err.message) || String(err),
              displayToUser: false,
              retryable: false,
              clearCache: false,
            });
          }
        }
      }
    };
    
    msalLogin();
  }, [isAuthenticated, retry, instance, accounts]);

  const redirectToEAWorld = () => {
    window.location.href = 'https://eaworld.work.ea.com/home/';
  };

  return (
    <div className="login-container">
      {storeAvailable.status ? (
        signInError.message ? (
          <LoginError error={signInError} onReturn={redirectToEAWorld} />
        ) : (
          <LoginLoading
            retry={retry}
            country={userInfo ? userInfo.country : ''}
            name={
              userInfo
                ? userInfo.givenName || userInfo.surname
                  ? `${userInfo.givenName || userInfo.surname}`
                  : ''
                : ''
            }
          />
        )
      ) : (
        <StoreUnavailable
          temporary={storeAvailable.temporary}
          onReturn={redirectToEAWorld}
        />
      )}
    </div>
  );
};

export default Login;
